import { db } from '../db';
import type { CompactSession } from '../db';

/** Synced rounds and their finished drafts are kept this long (7 days). */
export const RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

/** Wait after launch before clearing, so start-up stays quick. */
export const FIELD_SCOUT_CLEANUP_DELAY_MS = 5000;

const UNSENT = ['pending', 'syncing', 'failed', 'quarantined'] as const;

export interface FieldScoutCleanupResult {
  queueDeleted: number;
  draftsDeleted: number;
  compactDeleted: number;
  stamped: number;
}

const isOlderThan = (iso: string | undefined, cutoff: number): boolean => {
  if (!iso) return false;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t < cutoff;
};

/**
 * True when a finished compact session already became a scouting session
 * (same orchard, scouter and calendar date).
 */
export const isPromoted = async (cs: CompactSession): Promise<boolean> => {
  // Dexie throws on an undefined key; a row with no scouter cannot be matched.
  if (!cs.scouterName) return false;
  const datePrefix = (cs.lastUpdated || cs.startTime || '').slice(0, 10);
  const hit = await db.scoutingSessions
    .where('scouterName')
    .equals(cs.scouterName)
    .filter(
      (ss) =>
        ss.metadata?.orchardId === cs.orchardId &&
        (ss.date || '').slice(0, 10) === datePrefix
    )
    .first();
  return !!hit;
};

/** True when an unsent round exists for this orchard and scouter. */
const hasUnsentMatch = async (orchardName: string, scouterName: string): Promise<boolean> => {
  const hit = await db.fieldScoutQueue
    .where('syncStatus')
    .anyOf([...UNSENT])
    .filter((q) => q.payload?.orchardName === orchardName && q.payload?.scouterName === scouterName)
    .first();
  return !!hit;
};

/**
 * Clears uploaded rounds and finished drafts after 7 days, and retired compact
 * sessions that were already promoted. Never touches unsent rounds, open
 * drafts, or any media. Works one table at a time without loading whole tables.
 */
export const runFieldScoutCleanup = async (
  now: number = Date.now()
): Promise<FieldScoutCleanupResult> => {
  const cutoff = now - RETENTION_MS;
  const result: FieldScoutCleanupResult = {
    queueDeleted: 0,
    draftsDeleted: 0,
    compactDeleted: 0,
    stamped: 0,
  };

  // 1. Synced rounds: stamp the ones with no time, delete the expired ones.
  const syncedIds = await db.fieldScoutQueue.where('syncStatus').equals('synced').primaryKeys();
  for (const id of syncedIds) {
    const row = await db.fieldScoutQueue.get(id);
    if (!row || row.syncStatus !== 'synced') continue;
    if (!row.syncedAt) {
      await db.fieldScoutQueue.update(id, { syncedAt: new Date(now).toISOString() });
      result.stamped++;
    } else if (isOlderThan(row.syncedAt, cutoff)) {
      await db.fieldScoutQueue.delete(id);
      result.queueDeleted++;
    }
  }

  // 2. Finished drafts (mobile and desk). Media is never deleted here.
  for (const table of [db.fieldScoutDrafts, db.fieldScoutDeskDrafts] as const) {
    const ids = await table.where('isComplete').equals(1).primaryKeys();
    for (const id of ids) {
      const draft = await table.get(id as number);
      if (!draft || draft.isComplete !== 1) continue;
      let remove = false;
      if (draft.queueUuid) {
        const row = await db.fieldScoutQueue.where('uuid').equals(draft.queueUuid).first();
        remove = !row || (row.syncStatus === 'synced' && isOlderThan(row.syncedAt, cutoff));
      } else if (isOlderThan(draft.lastUpdated, cutoff)) {
        remove = !(await hasUnsentMatch(draft.orchardName, draft.scouterName));
      }
      if (remove) {
        await table.delete(id as number);
        result.draftsDeleted++;
      }
    }
  }

  // 3. Retired compact sessions that were already promoted.
  const compactIds = await db.compactSessions.where('isComplete').equals(1).primaryKeys();
  for (const id of compactIds) {
    const cs = await db.compactSessions.get(id as number);
    if (!cs) continue;
    if (await isPromoted(cs)) {
      await db.compactSessions.delete(id as number);
      result.compactDeleted++;
    }
  }

  return result;
};
